import React, { useState } from 'react';
import { REQUIREMENT_KINDS } from '../data/requirements';

// Requirements a design is checked against (data/requirements.js — the
// generated copy of backend/shared/blocks/requirements.js). One component,
// three homes: the pipeline editor's panel (rows + live verdict), the
// Administration card (org rows per environment, no verdict), the Solution
// board (rows across components). `results` carry the verdict per row id
// when the caller has a graph to judge; `fixed` rows render read-only.

export const STATUS_TONE = { met: 'ok', unmet: 'danger', 'needs-run': 'warn' };
export const STATUS_LABEL = { met: 'met', unmet: 'not met', 'needs-run': 'needs a run' };

export function StatusChip({ status }) {
  if (!status) return null;
  return <span className={`mf-chip is-${STATUS_TONE[status] || 'muted'}`} style={{ fontSize: 11 }}>{STATUS_LABEL[status] || status}</span>;
}

const newId = () => `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

export default function RequirementsEditor({ rows, onChange, results = null, fixed = [], onFocus = null, compact = false, emptyText }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ kind: 'approval', text: '', params: {} });
  const list = Array.isArray(rows) ? rows : [];
  const verdict = (id) => (results || []).find((r) => r.id === id) || null;
  const def = REQUIREMENT_KINDS[draft.kind] || { params: [] };

  const Row = ({ r, i, readOnly }) => {
    const v = verdict(r.id);
    const kind = REQUIREMENT_KINDS[r.kind] || { label: r.kind, params: [] };
    const params = Object.entries(r.params || {}).filter(([, x]) => x !== '' && x != null).map(([k, x]) => `${k}=${x}`).join(' · ');
    return (
      <div className={`nq-req ${v ? `is-${v.status}` : ''}`} style={{ marginTop: 8 }} data-nova-note={`Requirement ${r.id} (${kind.label}${r.source && r.source !== 'pipeline' ? `, from the ${r.source === 'env' ? 'environment' : 'organization'}` : ''}): "${r.text || kind.label}"${v ? ` — ${STATUS_LABEL[v.status] || v.status}: ${String(v.detail || '').slice(0, 110)}` : ''}`}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <StatusChip status={v?.status} />
          <strong style={{ fontSize: 12, color: 'var(--text)', flex: 1 }}>{r.text || kind.label}</strong>
          {readOnly ? <span className="mf-chip is-muted" style={{ fontSize: 11 }}>{r.source === 'env' ? 'environment' : 'organization'}</span>
            : <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" title="Remove this requirement" data-agent-guard="true" onClick={() => onChange(list.filter((_, idx) => idx !== i))}>✕</button>}
        </div>
        <div className="cs-help" style={{ fontSize: 11.5, marginTop: 2 }}>{kind.label}{params ? ` — ${params}` : ''}{r.storyId ? ' · from a story' : ''}</div>
        {v && (
          <div className="cs-help" style={{ fontSize: 11.5, marginTop: 2 }}>
            {v.detail}
            {onFocus && v.blockIds?.length > 0 && (
              <> · <button type="button" className="cs-linklike" onClick={() => onFocus(v.blockIds)}>show blocks</button></>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {!list.length && !fixed.length && <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>{emptyText || 'No requirements yet. Say what this design must guarantee — approvals, data integrity, performance — and the canvas is checked against it as you draw.'}</p>}
      {fixed.map((r) => <Row key={r.id} r={r} readOnly />)}
      {list.map((r, i) => <Row key={r.id || i} r={r} i={i} />)}
      {adding ? (
        <div className="cs-card" style={{ padding: 10, marginTop: 8, display: 'grid', gap: 6 }}>
          <select className="cs-input" value={draft.kind} onChange={(e) => setDraft({ kind: e.target.value, text: '', params: {} })} aria-label="Requirement kind">
            {Object.entries(REQUIREMENT_KINDS).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
          </select>
          <p className="cs-help" style={{ fontSize: 11.5, margin: 0 }}>{def.help}</p>
          <input className="cs-input" placeholder={`In your words (default: ${def.label})`} value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />
          {(def.params || []).map((p) => (
            <div key={p.key}>
              <label className="cs-help" style={{ display: 'block', fontSize: 11.5 }}>{p.label}{p.help ? ` — ${p.help}` : ''}</label>
              {p.type === 'select' ? (
                <select className="cs-input" value={draft.params[p.key] || p.options?.[0] || ''} onChange={(e) => setDraft({ ...draft, params: { ...draft.params, [p.key]: e.target.value } })}>
                  {(p.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input className="cs-input" value={draft.params[p.key] || ''} onChange={(e) => setDraft({ ...draft, params: { ...draft.params, [p.key]: e.target.value } })} />
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => { onChange([...list, { id: newId(), kind: draft.kind, text: draft.text.trim() || def.label, params: draft.params, source: 'pipeline' }]); setAdding(false); setDraft({ kind: 'approval', text: '', params: {} }); }}>Add</button>
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" style={{ marginTop: 8 }} onClick={() => setAdding(true)}>{compact ? '+ Requirement' : 'Add requirement'}</button>
      )}
    </div>
  );
}
